import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PathwaysApi } from 'src/api';
import { Pathway } from 'src/api/pathways/types';
import { getCurrentPathStep } from 'src/apps/Path/advancePathProgress';
import { mapPathwayToLearningPathData } from 'src/apps/Path/mapPathwayToLearningPathData';
import { resolveNextPathStepUrl } from 'src/apps/Path/resolveNextPathStep';
import { PathSessionType } from 'src/apps/Path/types';
import { ROUTES } from 'src/constants/routes.const';
import useSafePush from 'src/hooks/useSafePush';
import { convertUrlToTriggerRefresh } from 'src/utils/convertUrlToTriggerRefresh';
import { useCompletePathwayProgress } from './useCompletePathwayProgress';
import { useHasCorePathSessionTypes } from './useHasCorePathSessionTypes';

type UseContinueToNextPathStepParams = {
  languagePairId: number | null;
  pathwayNodeId: number | null;
  pathwayCheckpointId: number | null;
  /** When no pathway node/checkpoint id is present, complete the current matching node. */
  sessionType?: PathSessionType | null;
  /**
   * `skip` moves past a session the learner left without doing, instead of marking it
   * completed. Only the node they launched is skipped, and only while it's still current.
   */
  advanceBy?: 'complete' | 'skip';
};

type UseContinueToNextPathStepResult = {
  continueToNextStep: () => void;
  isResolving: boolean;
  /** Set once resolution finds the next step is a checkpoint, so the caller can
   * show the pre-checkpoint interstitial instead of navigating immediately. */
  pendingCheckpointUrl: string | null;
  confirmCheckpoint: () => void;
  isLaunchingCheckpoint: boolean;
};

/**
 * Completes the current path node/checkpoint (if not already completed) and
 * navigates straight into whatever comes next in the path - a session, a
 * checkpoint, or `/path` itself if the goal is complete or resolution fails.
 *
 * When the next step is a checkpoint, navigation pauses: `pendingCheckpointUrl`
 * is set instead of pushing, so the caller can show the pre-checkpoint screen
 * and call `confirmCheckpoint` once the learner chooses to continue.
 */
export const useContinueToNextPathStep = ({
  languagePairId,
  pathwayNodeId,
  pathwayCheckpointId,
  sessionType = null,
  advanceBy = 'complete',
}: UseContinueToNextPathStepParams): UseContinueToNextPathStepResult => {
  const queryClient = useQueryClient();
  const { safePush } = useSafePush();
  const coreSessionTypesOnly = useHasCorePathSessionTypes();
  const { completePathwayProgress } = useCompletePathwayProgress({
    languagePairId,
    pathwayNodeId,
    pathwayCheckpointId,
    sessionType,
  });
  const [isResolving, setIsResolving] = useState(false);
  const [pendingCheckpointUrl, setPendingCheckpointUrl] = useState<string | null>(null);
  const [isLaunchingCheckpoint, setIsLaunchingCheckpoint] = useState(false);
  const hasStartedRef = useRef(false);

  const launchUrl = useCallback(
    // Next learn/session is often the same Next.js page; without a fresh
    // `key` the client transition can no-op and leave this button spinning.
    (url: string) => safePush(convertUrlToTriggerRefresh(url)),
    [safePush],
  );

  const continueToNextStep = useCallback(() => {
    if (hasStartedRef.current) {
      return;
    }
    hasStartedRef.current = true;
    setIsResolving(true);

    const goToPathHard = () => safePush(ROUTES.path, true);

    const skipPathwayProgress = async (): Promise<Pathway | null> => {
      if (!languagePairId) {
        return null;
      }
      const pathway = await PathwaysApi.getActivePathway(languagePairId);
      const currentStep = getCurrentPathStep(
        mapPathwayToLearningPathData(pathway, { coreSessionTypesOnly }),
      );
      const isLaunchedNodeCurrent =
        currentStep?.kind === 'session' && Number(currentStep.session.id) === pathwayNodeId;
      if (!isLaunchedNodeCurrent) {
        return pathway;
      }
      const skipped = await PathwaysApi.skipNode(pathwayNodeId as number);
      queryClient.setQueryData(['activePathway', languagePairId], skipped);
      return skipped;
    };

    const resolve = async () => {
      const pathway =
        advanceBy === 'skip' ? await skipPathwayProgress() : await completePathwayProgress();
      if (!languagePairId || !pathway) {
        await goToPathHard();
        return;
      }

      const result = await resolveNextPathStepUrl(languagePairId, pathway, {
        coreSessionTypesOnly,
      });
      if (result.kind === 'goalComplete') {
        await goToPathHard();
        return;
      }

      if (result.updatedPathway) {
        queryClient.setQueryData(['activePathway', languagePairId], result.updatedPathway);
      }

      if (result.isCheckpoint) {
        setPendingCheckpointUrl(result.url);
        setIsResolving(false);
        return;
      }

      await launchUrl(result.url);
    };

    // Stay loading after the first click to block spam, but hard-navigate to
    // /path on failure so a stuck soft push cannot trap the learner forever.
    void resolve().catch(() => {
      void goToPathHard();
    });
  }, [
    advanceBy,
    completePathwayProgress,
    coreSessionTypesOnly,
    languagePairId,
    pathwayNodeId,
    queryClient,
    safePush,
    launchUrl,
  ]);

  const confirmCheckpoint = useCallback(() => {
    if (!pendingCheckpointUrl) {
      return;
    }
    setIsLaunchingCheckpoint(true);
    void launchUrl(pendingCheckpointUrl);
  }, [pendingCheckpointUrl, launchUrl]);

  return {
    continueToNextStep,
    isResolving,
    pendingCheckpointUrl,
    confirmCheckpoint,
    isLaunchingCheckpoint,
  };
};
